import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { ButtonStandard, Text, toast } from "@performyard/py-components";

import Breadcrumbs from "../../../../../components/Breadcrumbs";
import EmployeeSelection from "../../../../../components/EmployeeSelection";

import { useAssignRoleEmployees, useRole } from "../../../../../api/hooks/defs/roles";
import useNavigate from "../../../../../hooks/useNavigate";

import { deleteSelectionObject, fetchNewSelectionObject } from "../../../../../api/services/defs/selection-object";
import { getUserSelectionUsers } from "../../../../../api/services/defs/user-selection";

import { ROLES_LIST_PATH } from "../../CreateEditRole/validationSchema";

// Reads the entire selection in one paginated call. Generously sized — the backend has no
// upper bound on bulk assignment, and admins picking thousands of employees for one role is
// already an edge case. Bump if real customers hit it.
const SELECTION_LIMIT = 5000;

// Mirrors `ROLE_CUSTOM_FIELD_NAME` in packages/dto/src/dto/role.ts:10. Backend looks up the
// configured Role custom field by this exact name.
const ROLE_CUSTOM_FIELD_NAME = "Role";

// Stable empty exclusion list — fresh array references invalidate downstream memoization
// in EmployeeSelection.
const EMPTY_USER_IDS: string[] = [];

const ROLE_HEADER_OVERRIDES = { [ROLE_CUSTOM_FIELD_NAME]: "Current Role" };

const detailUrl = (roleId: string) => `/administration/roles/${roleId}?tab=employees`;

const AddEmployeesPage: React.FC = () => {
	const navigate = useNavigate();
	const { roleId = "" } = useParams<{ roleId: string }>();

	const roleQuery = useRole(roleId);

	const [selectionObjectId, setSelectionObjectId] = useState<string | null>(null);
	const [isInitializing, setIsInitializing] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const { mutateAsync: assignAsync } = useAssignRoleEmployees(roleId);

	const role = roleQuery.data?.role;
	const isArchived = role?.status === "archived";
	// Deep-link guard: the Add Employees entry point is hidden while Role is HRIS-managed.
	const isRoleFieldLocked = !!roleQuery.data?.roleFieldIntegrationConnected;
	const loadError = roleQuery.error;

	// `useNavigate` returns a fresh function each render — depending on it directly causes effects
	// to re-fire every render. Mirror values into refs and read through them inside one-shot effects.
	const navigateRef = useRef(navigate);
	const roleIdRef = useRef(roleId);
	useEffect(() => {
		navigateRef.current = navigate;
		roleIdRef.current = roleId;
	});

	useEffect(() => {
		if (loadError) {
			toast.error("Role not found");
			navigateRef.current(ROLES_LIST_PATH);
		} else if (isArchived) {
			toast.error("Reopen this role to add employees");
			navigateRef.current(detailUrl(roleIdRef.current));
		} else if (isRoleFieldLocked) {
			toast.error("Role assignments are managed by your HRIS integration");
			navigateRef.current(detailUrl(roleIdRef.current));
		}
	}, [loadError, isArchived, isRoleFieldLocked]);

	// Selection-object lifecycle: create temp record on mount, soft-delete on unmount. The DELETE
	// flips `deleted: true` on the record so it stops counting against any quotas / queries.
	useEffect(() => {
		let cancelled = false;
		fetchNewSelectionObject()
			.then((result) => {
				if (cancelled) return;
				setSelectionObjectId(result.user_selection_id);
			})
			.catch(() => {
				if (cancelled) return;
				toast.error("Could not start employee selection. Please try again.");
				navigateRef.current(detailUrl(roleIdRef.current));
			})
			.finally(() => {
				if (!cancelled) setIsInitializing(false);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		// Soft-delete the selection object when the user navigates away. Fire-and-forget; backend
		// TTL still catches orphans if this fails.
		return () => {
			if (selectionObjectId) {
				deleteSelectionObject(selectionObjectId).catch((err: unknown) => {
					console.warn("PY-11788 selection-object cleanup failed", err);
				});
			}
		};
	}, [selectionObjectId]);

	const handleCancel = () => {
		navigate(detailUrl(roleId));
	};

	const handleSubmit = async () => {
		if (!selectionObjectId || isSubmitting) return;
		setIsSubmitting(true);
		try {
			const result = await getUserSelectionUsers({
				selectionObjectID: selectionObjectId,
				limitSelected: true,
				limit: SELECTION_LIMIT,
				exclusionList: [],
				filters: {},
				sortField: "name",
				sortDirection: "asc",
			});
			const userIds = result.users.map((u) => u._id);
			if (userIds.length === 0) {
				toast.error("Select at least one employee.");
				setIsSubmitting(false);
				return;
			}
			// AssignEmployeesBody types user_ids as ObjectId[] (post-Zod-transform); we send strings and
			// the backend re-parses them. Cast bridges the type-level gap.
			await assignAsync({ user_ids: userIds } as unknown as Parameters<typeof assignAsync>[0]);
			toast.success(`${userIds.length} ${userIds.length === 1 ? "employee" : "employees"} assigned`);
			navigate(detailUrl(roleId));
		} catch (error) {
			toast.error(error instanceof Error ? error.message : "An error occurred while assigning employees.");
			setIsSubmitting(false);
		}
	};

	if (roleQuery.isLoading || loadError || isArchived || isRoleFieldLocked || !role) {
		return <div data-testid="add-employees-loading" />;
	}

	// Submit stays disabled until the selection object is ready. The picker itself is gated on
	// `selectionObjectId` below — it never mounts with a null id.
	const isPickerReady = !isInitializing && !!selectionObjectId;

	return (
		<div className="flex flex-col gap-4 px-6 py-8">
			<Breadcrumbs
				pages={[
					{ pageID: "breadcrumb-roles", label: "Roles", url: ROLES_LIST_PATH },
					{ pageID: "breadcrumb-role-name", label: role.name, url: detailUrl(roleId) },
					{ pageID: "breadcrumb-add-employees", label: "Add Employees", url: "" },
				]}
				className="mb-4"
			/>
			<div className="w-full text-center">
				<Text size="sub-heading" weight="semi-bold" color="neutral-9">
					{role.name}
				</Text>
				<Text size="paragraph-2" weight="regular" color="neutral-9">
					Select employees to add to this role. Each employee can only be in one role at a time.
				</Text>
			</div>

			{selectionObjectId && (
				<EmployeeSelection
					selectionObjectID={selectionObjectId}
					customFieldsIdentifiers={[ROLE_CUSTOM_FIELD_NAME, "Group", "Manager"]}
					customFieldHeaderOverrides={ROLE_HEADER_OVERRIDES}
					exclusionList={EMPTY_USER_IDS}
					filters={{}}
					paginationAlignment="center"
				/>
			)}

			<div className="flex justify-end gap-2">
				<div>
					<ButtonStandard size="medium" variant="outline" color="neutral" onClick={handleCancel}>
						Cancel
					</ButtonStandard>
				</div>
				<div>
					<ButtonStandard
						size="medium"
						color="primary"
						onClick={handleSubmit}
						disabled={!isPickerReady || isSubmitting}
						data-testid="add-employees-submit"
					>
						{isSubmitting ? "Assigning…" : "Select Employees"}
					</ButtonStandard>
				</div>
			</div>
		</div>
	);
};

export default AddEmployeesPage;
